import {AssetAttachmentType} from '@sollapay/enums';
import {Button, FieldInput, FieldSelect, FormSectionHeading, Icon} from '@sollapay/ui/components';
import {useEffect} from 'react';
import {Controller, useFieldArray} from 'react-hook-form';
import {useTranslation} from 'react-i18next';

import {AiFlashWrapper} from '@/features/ai-extraction';
import {getNestedFieldError} from '@/features/buyers/utils';

import type {AiFieldAccessor, AiSuggestion} from '@/features/ai-extraction';
import type {Control, FieldValues, UseFormRegister} from 'react-hook-form';

export interface AssetAttachmentsFieldsAi {
  fieldAi: (field: string) => AiFieldAccessor;
  isFlashing: (field: string) => boolean;
  /** Keys are the field paths AI has filled — used only to detect whether any of this field
   * array's own paths are among them. */
  aiFields: Map<string, AiSuggestion>;
  /** Reads the field array's own current value — e.g. `form.getValues`. */
  getValues: (name: string) => unknown;
}

export interface AssetAttachmentsFieldsProps<TFieldValues extends FieldValues> {
  control: Control<TFieldValues>;
  register: UseFormRegister<TFieldValues>;
  errors: unknown;
  /** Field array path — `'asset.attachments'` when nested, `'attachments'` on a flat form. */
  namePrefix: string;
  /** Omit to render plain fields with no AI wiring (e.g. the activation asset step, which isn't AI-extractable). */
  ai?: AssetAttachmentsFieldsAi;
  /** Visual-only required marker on the first row's Type label — the field is never enforced. */
  typeLabelRequired?: boolean;
}

interface AttachmentRowValue {
  type: AssetAttachmentType;
  description?: string;
}

/**
 * The physical sub-units (הצמדות) attached to an asset — balcony, yard, parking, storage —
 * shared by the buyer-creation flow's own asset section and the activation asset step. Every
 * field is optional (never enforced), matching the rest of the product.
 */
export function AssetAttachmentsFields<TFieldValues extends FieldValues>({
  control,
  register,
  errors,
  namePrefix,
  ai,
  typeLabelRequired = false
}: AssetAttachmentsFieldsProps<TFieldValues>) {
  const {t} = useTranslation('trustAccounts');
  const prefix = 'view.buyers.addBuyerModal.attachments';

  const {fields, append, remove, replace} = useFieldArray({control, name: namePrefix as never});

  const attachmentTypeOptions = [
    {value: AssetAttachmentType.Balcony, label: t(`${prefix}.types.balcony`)},
    {value: AssetAttachmentType.Yard, label: t(`${prefix}.types.yard`)},
    {value: AssetAttachmentType.Parking, label: t(`${prefix}.types.parking`)},
    {value: AssetAttachmentType.Storage, label: t(`${prefix}.types.storage`)},
    {value: AssetAttachmentType.Other, label: t(`${prefix}.types.other`)}
  ];

  // When AI fills attachment fields, sync useFieldArray with the populated form values.
  useEffect(() => {
    if (!ai) return;
    const hasAiAttachments = [...ai.aiFields.keys()].some(k => k.startsWith(namePrefix));
    if (!hasAiAttachments) return;
    const formAttachments = ai.getValues(namePrefix) as AttachmentRowValue[];
    if (formAttachments.length > fields.length) replace(formAttachments as never);
  }, [ai, fields.length, namePrefix, replace]);

  return (
    <div className="flex flex-col gap-4">
      <FormSectionHeading title={t(`${prefix}.title`)} />

      {fields.length > 0 && (
        <div className="flex flex-col gap-3">
          {fields.map((field, index) => {
            const typePath = `${namePrefix}.${index}.type`;
            const descriptionPath = `${namePrefix}.${index}.description`;
            // The first row carries the column labels, so its AI badges share the label line.
            const isFirstRow = index === 0;

            const typeAi = ai?.fieldAi(typePath);
            const descriptionAi = ai?.fieldAi(descriptionPath);

            return (
              <div key={field.id} className="grid grid-cols-2 items-end gap-4">
                <AiFlashWrapper flashing={ai?.isFlashing(typePath) ?? false}>
                  <Controller
                    name={typePath as never}
                    control={control}
                    render={({field: typeField}) => (
                      <FieldSelect
                        label={isFirstRow ? t(`${prefix}.type`) : undefined}
                        required={isFirstRow && typeLabelRequired}
                        placeholder={t(`${prefix}.typePlaceholder`)}
                        options={attachmentTypeOptions}
                        value={typeField.value as string}
                        onValueChange={value => {
                          typeField.onChange(value as AssetAttachmentType);
                          typeAi?.onAiChange();
                        }}
                        error={getNestedFieldError(errors, typePath)}
                        cornerBadge={typeAi?.aiIndicator}
                        isLoading={typeAi?.aiGenerating}
                      />
                    )}
                  />
                </AiFlashWrapper>
                <div className="flex items-end gap-2">
                  <div className="flex-1">
                    <AiFlashWrapper flashing={ai?.isFlashing(descriptionPath) ?? false}>
                      <FieldInput
                        label={isFirstRow ? t(`${prefix}.description`) : undefined}
                        placeholder={t(`${prefix}.descriptionPlaceholder`)}
                        error={getNestedFieldError(errors, descriptionPath)}
                        startAdornment={<Icon name="pen" className="size-4" />}
                        cornerBadge={descriptionAi?.aiIndicator}
                        isLoading={descriptionAi?.aiGenerating}
                        {...register(descriptionPath as never, {
                          onChange: () => descriptionAi?.onAiChange()
                        })}
                      />
                    </AiFlashWrapper>
                  </div>
                  <Button
                    type="button"
                    variant="secondary"
                    size="icon-sm"
                    onClick={() => remove(index)}
                    aria-label={t(`${prefix}.remove`)}
                    // Matches the input's height so the row lines up with no offsets.
                    className="size-[calc(--spacing(10)+2px)]"
                  >
                    <Icon name="x-close" className="size-4" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Button
        type="button"
        variant="secondary"
        size="sm"
        onClick={() => append({type: AssetAttachmentType.Parking, description: ''} as never)}
        icon={<Icon name="add" width={14} height={14} />}
        iconPosition="start"
      >
        {t(`${prefix}.add`)}
      </Button>
    </div>
  );
}
